// Isolated, dependency-free British-English number/amount spell-out utility.
// Used by the "Authorised Expenditure in Words" field on the Letter of Intent
// Details section to auto-generate wording from a numeric currency value.

const ONES = [
  '',
  'One',
  'Two',
  'Three',
  'Four',
  'Five',
  'Six',
  'Seven',
  'Eight',
  'Nine',
];

const TEENS = [
  'Ten',
  'Eleven',
  'Twelve',
  'Thirteen',
  'Fourteen',
  'Fifteen',
  'Sixteen',
  'Seventeen',
  'Eighteen',
  'Nineteen',
];

const TENS = [
  '',
  '',
  'Twenty',
  'Thirty',
  'Forty',
  'Fifty',
  'Sixty',
  'Seventy',
  'Eighty',
  'Ninety',
];

const SCALES = [
  '',
  'Thousand',
  'Million',
  'Billion',
  'Trillion',
  'Quadrillion',
  'Quintillion',
];

// 0-99
const twoDigitsToWords = (n) => {
  if (n === 0) return '';
  if (n < 10) return ONES[n];
  if (n < 20) return TEENS[n - 10];
  const tens = Math.floor(n / 10);
  const ones = n % 10;
  return ones > 0 ? `${TENS[tens]}-${ONES[ones]}` : TENS[tens];
};

// 0-999
const threeDigitsToWords = (n) => {
  const hundreds = Math.floor(n / 100);
  const remainder = n % 100;
  const parts = [];
  if (hundreds > 0) {
    parts.push(`${ONES[hundreds]} Hundred`);
    if (remainder > 0) parts.push('and');
  }
  if (remainder > 0) {
    parts.push(twoDigitsToWords(remainder));
  }
  return parts.join(' ');
};

/**
 * Converts a non-negative integer into British-English words, e.g.
 * 32242341 -> "Thirty-Two Million Two Hundred and Forty-Two Thousand Three
 * Hundred and Forty-One".
 * Returns '' for negative, non-finite, or non-numeric input.
 */
export const numberToWords = (value) => {
  const n = Math.floor(Math.abs(Number(value)));
  if (!Number.isFinite(n) || Number(value) < 0) return '';
  if (n === 0) return 'Zero';

  const groups = [];
  let remaining = n;
  while (remaining > 0) {
    groups.push(remaining % 1000);
    remaining = Math.floor(remaining / 1000);
  }

  const parts = [];
  for (let i = groups.length - 1; i >= 0; i -= 1) {
    const groupValue = groups[i];
    if (groupValue !== 0) {
      let groupWords = threeDigitsToWords(groupValue);
      // British convention: prefix "and" before a trailing group that has
      // no hundreds digit, when the number has more than one group
      // overall, e.g. 1001 -> "One Thousand and One".
      if (i === 0 && groups.length > 1 && groupValue < 100) {
        groupWords = `and ${groupWords}`;
      }

      const scale = SCALES[i] || '';
      parts.push(scale ? `${groupWords} ${scale}` : groupWords);
    }
  }

  return parts.join(' ');
};

/**
 * Converts a currency amount (number or numeric string, £/commas allowed)
 * into a fully spelled-out British-English wording, e.g.
 * 32242341 -> "Thirty-Two Million Two Hundred and Forty-Two Thousand Three
 * Hundred and Forty-One Pounds Sterling"; 1500.50 -> "One Thousand Five
 * Hundred Pounds and Fifty Pence".
 * Returns '' for empty, negative, or non-numeric input.
 */
export const amountInWords = (value) => {
  if (value === null || value === undefined || value === '') return '';

  const sanitized =
    typeof value === 'string' ? value.replace(/[£,\s]/g, '') : value;
  const amount = Number(sanitized);
  if (!Number.isFinite(amount) || amount < 0) return '';

  const rounded = Math.round(amount * 100) / 100;
  const pounds = Math.floor(rounded);
  const pence = Math.round((rounded - pounds) * 100);

  const poundsWords = `${numberToWords(pounds)} ${
    pounds === 1 ? 'Pound' : 'Pounds'
  }`;

  if (pence > 0) {
    const penceWords = `${numberToWords(pence)} ${
      pence === 1 ? 'Penny' : 'Pence'
    }`;
    return `${poundsWords} and ${penceWords}`;
  }

  return `${poundsWords} Sterling`;
};

export default amountInWords;
